import { useSelector } from "react-redux";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import styled from "styled-components";

import { LoaderWithOverlay } from "components/atoms";

import { getDashboardYearReportData } from "state/modules/dashboard";

import { FONT_FAMILY_MEDIUM } from "styles/typography";
import { MAIN_THEME } from "styles/colors";

const Container = styled.div`
	width: 100%;
	background: transparent;
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 12px;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	position: relative;
	padding: 24px;
`;

const Title = styled.h3`
	font-size: 20px;
	font-family: ${FONT_FAMILY_MEDIUM};
	font-weight: 600;
	color: ${MAIN_THEME.TEXT_PRIMARY};
	margin: 0 0 24px 0;
`;

const ChartWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 20px 0;
	height: 260px;
	width: 100%;
	margin-bottom: 24px;
`;

const ChartContainer = styled.div`
	width: 200px;
	height: 200px;
`;

const Legend = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	border-top: 1px solid ${MAIN_THEME.BORDER_COLOR};
	padding-top: 16px;
`;

const LegendItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-size: 14px;
`;

const LegendColor = styled.div<{ color: string }>`
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background-color: ${(props) => props.color};
	flex-shrink: 0;
`;

const LegendLabel = styled.span`
	color: ${MAIN_THEME.TEXT_PRIMARY};
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 1;
`;

const LegendValue = styled.span`
	color: ${MAIN_THEME.TEXT_PRIMARY};
	font-weight: 500;
`;

interface Props {
	isLoading: boolean;
}

const OccupancyRate = (props: Props) => {
	const { isLoading } = props;

	const report = useSelector(getDashboardYearReportData);

	const occupancyPercentage = Math.round(report?.occupancyRate || 0);
	const vacancyPercentage = 100 - occupancyPercentage;

	return (
		<Container>
			<Title>Occupancy Rate YTD</Title>
			<ChartWrapper>
				<ChartContainer>
					<CircularProgressbar
						value={occupancyPercentage}
						text={`${occupancyPercentage}%`}
						styles={buildStyles({
							rotation: 0.5,
							strokeLinecap: "round",
							textSize: "28px",
							pathTransitionDuration: 0.5,
							pathColor: MAIN_THEME.SECONDARY,
							textColor: MAIN_THEME.SECONDARY,
							trailColor: MAIN_THEME.DARK_BG_SECONDARY,
						})}
					/>
				</ChartContainer>
			</ChartWrapper>
			<Legend>
				<LegendItem>
					<LegendLabel>
						<LegendColor color={MAIN_THEME.SECONDARY} />
						Occupied
					</LegendLabel>
					<LegendValue>{occupancyPercentage}%</LegendValue>
				</LegendItem>
				<LegendItem>
					<LegendLabel>
						<LegendColor color={MAIN_THEME.DARK_BG_SECONDARY} />
						Vacant
					</LegendLabel>
					<LegendValue>{vacancyPercentage}%</LegendValue>
				</LegendItem>
			</Legend>
			{isLoading ? <LoaderWithOverlay /> : null}
		</Container>
	);
};

export default OccupancyRate;
